import { useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import { Trans } from '@lingui/react/macro'
import * as Dialog from '@radix-ui/react-dialog'

import { useBrandName } from '@jotta/grpc-connect-client/config'
import { useDisableDocumentScroll } from '@jotta/hooks'
import { Button } from '@jotta/ui/Button'

import { FormattedContent } from '../../Elements/FormattedContent/FormattedContent'

import { onboardingStore } from './OnboardingStore'
import WelcomeSvg from './welcome.svg'

export const WelcomeContent = observer<{ onClose: () => void }>(function ({
  onClose,
}) {
  const completed = onboardingStore.isCompleted('welcome')
  const brandName = useBrandName()

  useDisableDocumentScroll({ enabled: !completed })

  const onSkip = useCallback(() => {
    void onboardingStore.completeAll()
    onClose()
  }, [onClose])

  if (completed) {
    return null
  }

  return (
    <div className="fixed inset-0 z-modal flex justify-center overflow-y-scroll bg-white/95 p-4 backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-8 self-center">
        <WelcomeSvg className="h-auto w-[368px] max-w-full flex-shrink-0" />
        <div className="max-w-sm">
          <FormattedContent variant="formats.welcome">
            <Trans>
              <h2>Welcome to</h2>
              <h3>{brandName}!</h3>
            </Trans>
            <p>
              <Trans>
                You now have an easy way to keep your data safe, and share files
                with your friends and family.
              </Trans>
            </p>
          </FormattedContent>
          <div className="mt-6 flex gap-3">
            <Button className="btn btn-primary" onClick={onClose}>
              <Trans>Get Started!</Trans>
            </Button>
            <Button className="btn btn-outline" onClick={onSkip}>
              <Trans>Skip</Trans>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
})

export const WelcomeDialog = observer(function WelcomeDialog() {
  const completed = onboardingStore.isCompleted('welcome')

  const completeTask = useCallback(() => {
    void onboardingStore.complete('welcome')
  }, [])

  if (completed) {
    return null
  }

  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Content asChild>
          <WelcomeContent onClose={completeTask} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
})
